/**
 * Hand-rolled SVG charts. No charting library — the prototype had none, and the four
 * shapes here are the whole inventory.
 *
 * Every colour is a CSS token so both themes work without a second implementation.
 */
import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMeasure } from '@/ui/useMeasure'
import { downloadSVGAsPNG, type LegendEntry } from '@/ui/exportPng'
import { EnlargeButton, PopOutHead, usePopOutLifecycle } from './PopOut'

/* ------------------------------------------------------------- floating tip */

/**
 * The Ledger's hover tip: a small fixed box beside the cursor, flipped to the left
 * when it would run off the window and dropped below the cursor when it would run
 * off the top. Rendered into <body> so no ancestor transform can pin it in place.
 */
function FloatingTip({ x, y, children }: { x: number; y: number; children: React.ReactNode }) {
  const [box, setBox] = useState<HTMLDivElement | null>(null)
  const w = box?.offsetWidth ?? 0
  const h = box?.offsetHeight ?? 0
  let left = x + 14
  let top = y - h - 12
  if (left + w > window.innerWidth - 10) left = x - w - 14
  if (left < 8) left = 8
  if (top < 8) top = y + 18
  return createPortal(
    <div
      ref={setBox}
      className="chart-tip"
      role="status"
      aria-live="polite"
      style={{ left, top, visibility: box ? 'visible' : 'hidden' }}
    >
      {children}
    </div>,
    document.body,
  )
}

export const SERIES = ['--s1', '--s2', '--s3', '--s4', '--s5', '--s6', '--s7', '--s8']
export const ORD = ['--ord1', '--ord2', '--ord3']

/**
 * A domain and tick set on round numbers.
 *
 * The axis used to divide the observed range into five equal parts, which produced
 * ticks like $27.56 / $55.12 / $82.68 — five numbers a reader has to actually read
 * before they can place a point. Round steps are the point of an axis: they are the
 * values you did NOT label directly, so they have to be cheap to read.
 */
export function niceScale(min: number, max: number, target = 4) {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    return { lo: 0, hi: 1, ticks: [0, 1] }
  }
  const raw = (max - min) / target
  const mag = Math.pow(10, Math.floor(Math.log10(raw)))
  // 1 / 2 / 2.5 / 5 / 10 — the steps people count in.
  const step = ([1, 2, 2.5, 5, 10].find((m) => raw <= m * mag) ?? 10) * mag
  const lo = Math.floor(min / step) * step
  const hi = Math.ceil(max / step) * step
  const ticks: number[] = []
  // Accumulate off the index, not by repeated addition — adding 0.1 sixteen times
  // lands on 1.5999999999999999 and the label prints it.
  for (let i = 0; lo + i * step <= hi + step / 1e6; i++) ticks.push(lo + i * step)
  return { lo, hi, ticks }
}

/** A ninth series folds into "Other" rather than cycling the palette. */
export const seriesVar = (i: number) => `var(${SERIES[i % SERIES.length]})`

/**
 * Ratio scales against an org rate are symmetric: above 1.35x is red, below 1/1.35x
 * is green, the middle is blue. One colour each way, equal weight.
 */
export const RATIO_THRESHOLD = 1.35
export function ratioColor(value: number | null, orgRate: number | null): string {
  if (value === null || orgRate === null || orgRate === 0) return 'var(--ink-3)'
  const r = value / orgRate
  if (r > RATIO_THRESHOLD) return 'var(--crit)'
  if (r < 1 / RATIO_THRESHOLD) return 'var(--good)'
  return 'var(--accent)'
}

/* ------------------------------------------------------------- horizontal bars */

export interface BarDatum {
  key: string
  label: string
  /** Bar length. */
  value: number
  /** Printed at the right of the bar. */
  rightLabel: string
  color?: string
  sub?: string
}

export function BarChart({
  data,
  selected,
  onSelect,
  height = 26,
}: {
  data: BarDatum[]
  selected?: string | null
  onSelect?: (key: string) => void
  height?: number
}) {
  const max = Math.max(1, ...data.map((d) => d.value))
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {data.map((d) => {
        const dim = selected != null && selected !== d.key
        return (
          <div
            key={d.key}
            onClick={onSelect ? () => onSelect(d.key) : undefined}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(84px, 180px) minmax(40px, 1fr) minmax(62px, 92px)',
              alignItems: 'center',
              gap: 10,
              cursor: onSelect ? 'pointer' : 'default',
              opacity: dim ? 0.32 : 1,
              transition: 'opacity .12s',
            }}
          >
            <div style={{ overflow: 'hidden' }}>
              <div
                style={{
                  fontSize: 'var(--t-chart-label)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={d.label}
              >
                {d.label}
              </div>
              {d.sub && (
                <div className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-3)' }}>
                  {d.sub}
                </div>
              )}
            </div>
            <div style={{ background: 'var(--surface-3)', borderRadius: 3, height }}>
              <div
                style={{
                  width: `${(d.value / max) * 100}%`,
                  height: '100%',
                  borderRadius: 3,
                  background: d.color ?? 'var(--accent)',
                  outline: selected === d.key ? '2px solid var(--ink)' : 'none',
                  outlineOffset: 1,
                }}
              />
            </div>
            <div
              className="mono"
              style={{ fontSize: 'var(--t-chart-value)', textAlign: 'right', color: 'var(--ink)' }}
            >
              {d.rightLabel}
            </div>
          </div>
        )
      })}
      {data.length === 0 && (
        <p className="body-note" style={{ color: 'var(--ink-3)' }}>
          Nothing to show for this selection.
        </p>
      )}
    </div>
  )
}

/* --------------------------------------------------------------- ranked bars */

export interface RankBar {
  key: string
  label: string
  /** Bar length. */
  value: number
  /** Printed at the right edge. */
  right: string
  /** Printed left of `right`, in its own column — the slice's claims (review #231). */
  count?: string
  color: string
  /** Tooltip rows, printed under the label. */
  tip?: [string, string][]
}

/**
 * The Ledger's ranked bar list: one SVG, a label column, a money axis with three
 * ticks, thin rounded bars, the figure at the right edge, and a hover tip. A picked
 * row is boxed in the accent and every other row fades.
 */
export function RankBars({
  items,
  selected,
  onSelect,
  labelWidth = 200,
  formatAxis,
  clickHint = 'Click to see who works these',
  heads,
  pinned = [],
  pinnedLabel,
  sort,
  onSort,
}: {
  items: RankBar[]
  selected?: string | null
  onSelect?: (key: string) => void
  labelWidth?: number
  formatAxis: (v: number) => string
  /** The tip's last line on an unpicked row — says what clicking does. */
  clickHint?: string
  /** Column heads over the figures at the right, where there are two of them. */
  heads?: { count: string; right: string }
  /**
   * Bars drawn after the ranked ones, under a thin rule and `pinnedLabel`: buckets
   * that are not a slice of their own (the Overview's Unknown and no-claim payroll).
   */
  pinned?: RankBar[]
  pinnedLabel?: string
  /**
   * Which head the bars are sorted by, when the heads sort (review #402). Null is the
   * caller's own order. The caller sorts; this only draws the heads as buttons.
   */
  sort?: { key: 'count' | 'right'; dir: 'asc' | 'desc' } | null
  /** Makes the heads clickable: called with the head clicked. */
  onSort?: (key: 'count' | 'right') => void
}) {
  const [hover, setHover] = useState<{ key: string; cx: number; cy: number } | null>(null)
  useEffect(() => {
    if (hover === null) return
    const off = () => setHover(null)
    document.addEventListener('scroll', off, true)
    return () => document.removeEventListener('scroll', off, true)
  }, [hover])

  const rows = pinned.length ? [...items, ...pinned] : items
  if (!rows.length) {
    return (
      <p className="body-note" style={{ color: 'var(--ink-3)', textAlign: 'center', padding: 26 }}>
        No rows match the current filters.
      </p>
    )
  }

  const W = 760
  const counts = items.some((d) => d.count !== undefined)
  // The count column sits left of the figure; both right-aligned in their own lane —
  // wider when the heads sort, so "Cost per claim ↓" clears the count's head.
  const lane = onSort ? 112 : 92
  const countX = W - 6 - lane
  const valW = counts ? 118 + lane : 118
  const rowH = 26
  // Room for the column heads, when there are any.
  const top = heads ? 14 : 0
  const pad = 8 + top
  // Room for the rule and its label before the pinned bars, when there are any.
  const gap = pinned.length ? 20 : 0
  const H = rows.length * rowH + gap + pad * 2 - top + 16
  const max = niceScale(0, Math.max(1, ...rows.map((d) => Math.abs(d.value)))).hi
  const x0 = labelWidth
  const x1 = W - valW
  const tipItem = hover ? rows.find((d) => d.key === hover.key) : undefined
  const ruleY = pad + items.length * rowH + 6

  return (
    <>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        style={{ overflow: 'visible' }}
        role={onSort ? 'group' : 'img'}
      >
        {heads && (
          <g fontSize={9.5} fill="var(--ink-3)" fontFamily="var(--mono)" textAnchor="end">
            {(counts ? (['count', 'right'] as const) : (['right'] as const)).map((k) => {
              const x = k === 'count' ? countX : W - 6
              const name = k === 'count' ? heads.count : heads.right
              if (!onSort) {
                return (
                  <text key={k} x={x} y={10}>
                    {name}
                  </text>
                )
              }
              const on = sort?.key === k ? sort.dir : null
              const next = on === 'desc' ? 'smallest first' : on === 'asc' ? 'the default order' : 'largest first'
              return (
                <g
                  key={k}
                  className="rank-head"
                  role="button"
                  tabIndex={0}
                  aria-label={`${name}: ${on === 'desc' ? 'sorted largest first' : on === 'asc' ? 'sorted smallest first' : 'not sorted'}. Click for ${next}.`}
                  data-testid={`rank-head-${k}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => onSort(k)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onSort(k)
                    }
                  }}
                >
                  <title>{`Sort by ${name.toLowerCase()} — click for ${next}`}</title>
                  <rect x={x - lane + 6} y={-2} width={lane - 2} height={16} fill="transparent" />
                  <text x={x} y={10} fill={on ? 'var(--accent-ink)' : undefined} fontWeight={on ? 600 : undefined}>
                    {name}
                    <tspan fill={on ? 'var(--accent)' : 'var(--ink-3)'}>
                      {on === 'desc' ? ' ↓' : on === 'asc' ? ' ↑' : ' ↕'}
                    </tspan>
                  </text>
                </g>
              )
            })}
          </g>
        )}
        <line x1={x0} x2={x0} y1={pad} y2={H - pad + top - 16} stroke="var(--rule-2)" strokeWidth={1} />
        {[0, 0.5, 1].map((t) => {
          const X = x0 + (x1 - x0) * t
          return (
            <g key={t}>
              {t > 0 && (
                <line x1={X} x2={X} y1={pad} y2={H - pad + top - 16} stroke="var(--rule)" strokeWidth={1} />
              )}
              <text
                x={X}
                y={H - pad + top - 3}
                textAnchor={t === 1 ? 'end' : 'middle'}
                fontSize={10}
                fill="var(--ink-3)"
                fontFamily="var(--mono)"
              >
                {formatAxis(max * t)}
              </text>
            </g>
          )
        })}
        {pinned.length > 0 && (
          <g data-testid="rank-pinned-rule">
            <line x1={0} x2={W} y1={ruleY} y2={ruleY} stroke="var(--rule-2)" strokeWidth={1} strokeDasharray="3 3" />
            {pinnedLabel && (
              <text
                x={labelWidth - 10}
                y={ruleY + 11}
                textAnchor="end"
                fontSize={9.5}
                fill="var(--ink-3)"
                fontFamily="var(--mono)"
              >
                {pinnedLabel}
              </text>
            )}
          </g>
        )}
        {rows.map((d, i) => {
          const y = pad + i * rowH + (i >= items.length ? gap : 0)
          const bh = Math.min(13, rowH - 11)
          const w = Math.max(1.5, (Math.abs(d.value) / max) * (x1 - x0))
          const on = selected === d.key
          const dim = selected != null && !on
          const label = d.label.length > 26 ? d.label.slice(0, 25) + '…' : d.label
          return (
            <g
              key={d.key}
              style={{ cursor: onSelect ? 'pointer' : 'default' }}
              onMouseMove={(e) => setHover({ key: d.key, cx: e.clientX, cy: e.clientY })}
              onMouseLeave={() => setHover(null)}
              onClick={
                onSelect
                  ? () => {
                      setHover(null)
                      onSelect(d.key)
                    }
                  : undefined
              }
            >
              <rect x={0} y={y} width={W} height={rowH} fill="transparent" />
              {on && (
                <rect
                  x={0}
                  y={y + 1}
                  width={W}
                  height={rowH - 2}
                  rx={5}
                  fill="var(--accent-soft)"
                  stroke="var(--accent)"
                  strokeWidth={1}
                />
              )}
              <text
                x={labelWidth - 10}
                y={y + rowH / 2 + 3.5}
                textAnchor="end"
                fontSize={10}
                fill="var(--ink-2)"
                fontFamily="var(--mono)"
                opacity={dim ? 0.45 : 1}
              >
                {label}
              </text>
              <rect
                x={x0}
                y={y + (rowH - bh) / 2}
                width={w}
                height={bh}
                rx={3.5}
                fill={on ? 'var(--accent)' : d.color}
                opacity={dim ? 0.3 : 1}
              />
              {d.count !== undefined && (
                <text
                  x={countX}
                  y={y + rowH / 2 + 3.5}
                  textAnchor="end"
                  fontSize={10}
                  fill="var(--ink-2)"
                  fontFamily="var(--mono)"
                  opacity={dim ? 0.45 : 1}
                >
                  {d.count}
                </text>
              )}
              <text
                x={W - 6}
                y={y + rowH / 2 + 3.5}
                textAnchor="end"
                fontSize={10}
                fill="var(--ink)"
                fontFamily="var(--mono)"
                opacity={dim ? 0.45 : 1}
              >
                {d.right}
              </text>
            </g>
          )
        })}
      </svg>
      {hover && tipItem && (
        <FloatingTip x={hover.cx} y={hover.cy}>
          <div className="chart-tip-title">{tipItem.label}</div>
          {(tipItem.tip ?? []).map(([k, v]) => (
            <div key={k} className="chart-tip-row">
              <span>{k}</span>
              <span>{v}</span>
            </div>
          ))}
          {onSelect && (
            <div style={{ color: 'var(--ink-3)', fontSize: 11, marginTop: 5 }}>
              {selected === tipItem.key ? 'Click to clear' : clickHint}
            </div>
          )}
        </FloatingTip>
      )}
    </>
  )
}

/* ---------------------------------------------------------------- line charts */

export interface Series {
  key: string
  label: string
  /** Null gaps are skipped rather than drawn as zero. */
  points: (number | null)[]
  /**
   * The claims behind each point, shown beside it in the hover tip (review #231:
   * "add number of claims … to the tool tip on the trend chart"). Optional.
   */
  counts?: (number | null)[]
}

export function LineChart({
  series,
  labels,
  /** Index of the first month not closed at export. Shaded and captioned "partial". */
  shadedFrom,
  format,
  /** Short form for the per-point labels, which have far less room than an axis tick. */
  formatPoint,
  height = 210,
  showPointLabels = true,
  labelEverySeries = false,
  countLabel = 'claims',
}: {
  series: Series[]
  labels: string[]
  shadedFrom?: number
  format: (v: number) => string
  formatPoint?: (v: number) => string
  height?: number
  showPointLabels?: boolean
  /** Label every point of every series, as the Ledger's department trend does. */
  labelEverySeries?: boolean
  /** What a series' `counts` count, in the tip: "1,204 claims". */
  countLabel?: string
}) {
  const [hidden, setHidden] = useState<Set<string>>(new Set())
  // Hovered month plus where the cursor is, so the tip can sit beside it.
  const [hover, setHover] = useState<{ i: number; cx: number; cy: number } | null>(null)
  // Draw at the real pixel width. A fixed viewBox scales its text with everything else,
  // so 10.5px type in a 760-wide box renders at ~4px inside a 330px small-multiple.
  const [box, W] = useMeasure<HTMLDivElement>(760)

  // A fixed tip does not scroll with the page; hide it rather than let it drift.
  useEffect(() => {
    if (hover === null) return
    const off = () => setHover(null)
    document.addEventListener('scroll', off, true)
    return () => document.removeEventListener('scroll', off, true)
  }, [hover])

  const visible = series.filter((s) => !hidden.has(s.key))
  const H = height
  const narrow = W < 460

  const all = visible.flatMap((s) => s.points).filter((v): v is number => v !== null)
  const { lo: min, hi: max, ticks } = niceScale(Math.min(0, ...all), Math.max(1, ...all))

  // Size the left gutter to the widest tick label it has to hold. A fixed gutter
  // clips "$1,234,567" while wasting space on "8%".
  const longestTick = Math.max(...ticks.map((v) => format(v).length))
  const showEndLabels = !narrow && series.length > 1

  /*
   * Size the right gutter to the end labels it actually has to hold.
   *
   * It was a flat 74px. The labels now carry the series' final value as well as its
   * name, and a fixed gutter either clips "Medicaid - Sign… $41" or wastes a third of
   * the plot on "HMO $67". Measured the same way as the left one.
   */
  const longestEnd = showEndLabels
    ? Math.max(
        0,
        ...visible.map((s) => {
          const last = [...s.points].reverse().find((v): v is number => v !== null && Number.isFinite(v))
          const name = s.label.length > 13 ? 13 : s.label.length
          return name + 1 + (last === undefined ? 0 : (formatPoint ?? format)(last).length)
        }),
      )
    : 0

  const pad = {
    // IBM Plex Mono advances ~0.6em per character, so 7.0px covers the largest tick
    // size any density tier uses (11.5px). Over-reserving the gutter costs a few pixels
    // of plot; under-reserving clips the tick labels, which is not recoverable.
    l: Math.ceil(longestTick * 7.0) + 12,
    // No end labels when narrow, but the gutter still has to fit half of the last
    // centred x-axis label.
    r: showEndLabels ? Math.ceil(longestEnd * 7.0) + 20 : 24,
    t: 14,
    b: 26,
  }
  const step = (W - pad.l - pad.r) / Math.max(1, labels.length - 1)
  const x = (i: number) => pad.l + i * step
  const y = (v: number) =>
    pad.t + (1 - (v - min) / (max - min || 1)) * (H - pad.t - pad.b)

  const fmtPoint = formatPoint ?? format
  /*
   * A number on every point of every series is not labelling, it is a second dataset
   * drawn in text: five departments across eight months put forty numerals inside the
   * plot and the lines disappeared underneath them.
   *
   * So point labels belong to the ONE-series case only — there they are the story, and
   * there is no legend or end label competing for the same pixels. With two or more
   * series the end label carries the series' final value, the legend carries identity,
   * and the crosshair tooltip carries every other value on demand.
   */
  const wantPointLabels = showPointLabels && !narrow && (series.length === 1 || labelEverySeries)

  /*
   * Lay the drawing out before rendering, so labels can be placed against each other
   * rather than hopefully.
   *
   *  - point labels: one column of taken y-positions per month; a label that cannot
   *    find a free slot is dropped rather than printed over its neighbour.
   *  - end labels: sorted, then pushed to a 13px minimum gap. A label moved off its
   *    line keeps a LEADER back to it — stacking them silently is how an end label
   *    starts pointing at the wrong series.
   */
  const laid = useMemo(() => {
    const columns: number[][] = labels.map(() => [])
    const points: { key: string; x: number; y: number; colour: string; text: string }[] = []
    const ends: {
      key: string
      x: number
      y: number
      /** Where the line actually ends, so a nudged label can draw a leader to it. */
      anchorX: number
      anchorY: number
      colour: string
      text: string
    }[] = []

    for (const s of visible) {
      const colour = seriesVar(series.indexOf(s))
      let lastIndex = -1

      s.points.forEach((v, i) => {
        if (v === null || !Number.isFinite(v)) return
        lastIndex = i
        if (!wantPointLabels) return

        const column = columns[i]!
        let ly = y(v) - 9
        let tries = 0
        while (tries < 4 && column.some((o) => Math.abs(o - ly) < 11)) {
          ly -= (tries % 2 ? -1 : 1) * (12 + tries * 4)
          tries++
        }
        if (column.some((o) => Math.abs(o - ly) < 11)) return
        if (ly <= pad.t + 4 || ly >= H - pad.b) return
        column.push(ly)
        points.push({ key: `${s.key}:${i}`, x: x(i), y: ly, colour, text: fmtPoint(v) })
      })

      if (lastIndex >= 0 && showEndLabels && s.label) {
        const value = s.points[lastIndex] as number
        const name = s.label.length > 13 ? s.label.slice(0, 12) + '…' : s.label
        ends.push({
          key: s.key,
          x: x(lastIndex) + 9,
          y: y(value),
          anchorX: x(lastIndex),
          anchorY: y(value),
          colour,
          // Name AND final value: this is the only label the series gets, so it should
          // answer both "which line is this" and "where did it end up".
          text: `${name} ${fmtPoint(value)}`,
        })
      }
    }

    ends.sort((a, b) => a.y - b.y)
    for (let i = 1; i < ends.length; i++) {
      if (ends[i]!.y - ends[i - 1]!.y < 13) ends[i]!.y = ends[i - 1]!.y + 13
    }
    const over = ends.length ? ends[ends.length - 1]!.y - (H - pad.b) : 0
    if (over > 0) for (const e of ends) e.y -= over

    return { points, ends }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, labels, W, H, min, max, wantPointLabels, showEndLabels])

  return (
    <div ref={box}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width={W}
        height={H}
        style={{ width: '100%', height: 'auto', overflow: 'visible' }}
        onMouseLeave={() => setHover(null)}
      >
        {/*
          A month that was not closed at export is shaded from half a column before it,
          and captioned — the rate is real, the volume is not, and an unlabelled grey
          band only tells you something is different, not what.
        */}
        {shadedFrom !== undefined && shadedFrom >= 0 && shadedFrom < labels.length && (
          <>
            <rect
              x={Math.max(pad.l, x(shadedFrom) - step / 2)}
              y={pad.t}
              width={Math.max(0, W - pad.r - Math.max(pad.l, x(shadedFrom) - step / 2))}
              height={H - pad.t - pad.b}
              fill="var(--surface-3)"
              opacity={0.75}
            />
            {/* At the FOOT of the band, not the head: the top-right corner is where
                the end labels live, and the caption was landing underneath them. */}
            <text
              x={x(shadedFrom)}
              y={H - pad.b - 7}
              textAnchor="middle"
              fontSize="var(--t-chart-tick)"
              fill="var(--ink-3)"
              fontFamily="var(--mono)"
            >
              partial
            </text>
          </>
        )}

        {ticks.map((v) => (
          <g key={v}>
            <line
              x1={pad.l}
              x2={W - pad.r}
              y1={y(v)}
              y2={y(v)}
              stroke="var(--rule)"
              strokeWidth={1}
            />
            <text
              x={pad.l - 8}
              y={y(v) + 3}
              textAnchor="end"
              fontSize="var(--t-chart-tick)"
              fill="var(--ink-3)"
              fontFamily="var(--mono)"
            >
              {format(v)}
            </text>
          </g>
        ))}

        <line
          x1={pad.l}
          x2={W - pad.r}
          y1={H - pad.b}
          y2={H - pad.b}
          stroke="var(--rule-2)"
          strokeWidth={1}
        />

        {labels.map((l, i) => (
          <text
            key={l}
            x={x(i)}
            y={H - 8}
            textAnchor="middle"
            fontSize="var(--t-chart-tick)"
            fill="var(--ink-3)"
            fontFamily="var(--mono)"
          >
            {l}
          </text>
        ))}

        {hover !== null && (
          <line
            x1={x(hover.i)}
            x2={x(hover.i)}
            y1={pad.t}
            y2={H - pad.b}
            stroke="var(--ink-3)"
            strokeWidth={1}
            strokeDasharray="3 3"
          />
        )}

        {visible.map((s) => {
          const colour = seriesVar(series.indexOf(s))
          const pts = s.points
            .map((v, i) => (v === null ? null : ([x(i), y(v)] as const)))
            .filter((p): p is readonly [number, number] => p !== null)
          if (!pts.length) return null
          const d = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0]},${p[1]}`).join(' ')
          return (
            <g key={s.key}>
              <path
                d={d}
                fill="none"
                stroke={colour}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {pts.map((p, i) => (
                <circle
                  key={i}
                  cx={p[0]}
                  cy={p[1]}
                  r={3}
                  fill={colour}
                  stroke="var(--surface)"
                  strokeWidth={2}
                />
              ))}
            </g>
          )
        })}

        {/*
          Point labels last, so their halo sits over the lines rather than under.

          They are drawn in ink, NOT the series colour. A mark may be any colour, but a
          number is text: marigold on white measures 1.50:1 and husk 1.92:1. The line
          and dot underneath already say which series this is, and the label sits on
          them, so the colour was carrying nothing the position did not.
        */}
        {laid.points.map((p) => (
          <text
            key={p.key}
            x={p.x}
            y={p.y}
            textAnchor="middle"
            fontSize="var(--t-chart-tick)"
            fontWeight={600}
            fill="var(--ink)"
            fontFamily="var(--mono)"
            stroke="var(--surface)"
            strokeWidth={3}
            paintOrder="stroke"
            strokeLinejoin="round"
          >
            {p.text}
          </text>
        ))}

        {/*
          End labels keep the series mapping through a coloured dot rather than coloured
          text, for the same reason: several of these hues cannot carry legible text.
        */}
        {laid.ends.map((e) => (
          <g key={e.key}>
            {/* Leader, drawn only when the label had to move off its own line. */}
            {Math.abs(e.y - e.anchorY) > 1.5 && (
              <path
                d={`M${e.anchorX + 4},${e.anchorY} L${e.x - 2},${e.y - 3.5}`}
                fill="none"
                stroke={e.colour}
                strokeWidth={1}
                opacity={0.55}
              />
            )}
            <circle cx={e.x + 3} cy={e.y - 3.5} r={3.5} fill={e.colour} />
            <text
              x={e.x + 11}
              y={e.y}
              fontSize="var(--t-chart-tick)"
              fill="var(--ink-2)"
              fontFamily="var(--mono)"
            >
              {e.text}
            </text>
          </g>
        ))}

        {/* One hit surface over the whole plot; the tip snaps to the nearest month. */}
        <rect
          x={pad.l - 10}
          y={0}
          width={W - pad.l - pad.r + 20}
          height={H}
          fill="transparent"
          style={{ cursor: 'crosshair' }}
          onMouseMove={(e) => {
            const r = e.currentTarget.ownerSVGElement!.getBoundingClientRect()
            const px = (e.clientX - r.left) * (W / r.width)
            let best = 0
            for (let i = 1; i < labels.length; i++)
              if (Math.abs(x(i) - px) < Math.abs(x(best) - px)) best = i
            setHover({ i: best, cx: e.clientX, cy: e.clientY })
          }}
        />
      </svg>

      {series.length > 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 8 }}>
          {series.map((s, i) => {
            const off = hidden.has(s.key)
            return (
              <button
                key={s.key}
                onClick={() =>
                  setHidden((h) => {
                    const n = new Set(h)
                    if (n.has(s.key)) n.delete(s.key)
                    else n.add(s.key)
                    return n
                  })
                }
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  background: 'none',
                  border: 0,
                  padding: 0,
                  cursor: 'pointer',
                  fontFamily: 'var(--mono)',
                  fontSize: 'var(--t-micro)',
                  /*
                   * A hidden series is struck through and muted, NOT faded.
                   * `opacity: 0.38` put this at 2.1:1 — a control you are being asked to
                   * click back on, rendered almost invisible. Strikethrough says "off"
                   * without spending contrast, and the swatch keeps its hue so you can
                   * still tell which line it is.
                   */
                  color: off ? 'var(--ink-3)' : 'var(--ink-2)',
                  textDecoration: off ? 'line-through' : 'none',
                }}
                aria-pressed={!off}
              >
                <span
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: 2,
                    background: seriesVar(i),
                    // The one thing that may fade: a swatch is not text.
                    opacity: off ? 0.4 : 1,
                  }}
                />
                {s.label}
              </button>
            )
          })}
        </div>
      )}

      {hover !== null && (
        <FloatingTip x={hover.cx} y={hover.cy}>
          <div className="chart-tip-title">
            {labels[hover.i]}
            {shadedFrom !== undefined && shadedFrom >= 0 && hover.i >= shadedFrom && (
              <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}> (partial)</span>
            )}
          </div>
          {visible
            .map((s) => ({ s, v: s.points[hover.i], colour: seriesVar(series.indexOf(s)) }))
            .filter((r): r is { s: Series; v: number; colour: string } => typeof r.v === 'number' && Number.isFinite(r.v))
            .sort((a, b) => b.v - a.v)
            .map(({ s, v, colour }) => {
              const n = s.counts?.[hover.i]
              return (
                <div key={s.key} className="chart-tip-row">
                  <span>
                    <i className="chart-tip-swatch" style={{ background: colour }} />
                    {s.label}
                  </span>
                  <span>
                    {format(v)}
                    {typeof n === 'number' && Number.isFinite(n) && (
                      <span className="chart-tip-count" data-testid="tip-count">
                        {' · '}
                        {Math.round(n).toLocaleString('en-US')} {countLabel}
                      </span>
                    )}
                  </span>
                </div>
              )
            })}
        </FloatingTip>
      )}
    </div>
  )
}

/* ------------------------------------------------------- 100% stacked segments */

export interface Segment {
  key: string
  label: string
  value: number
  color: string
  /** Label colour for this step. Falls back to white when not given. */
  fg?: string
}

/** One row of segments summing to 100%. Used for touch mix and recency. */
export function StackedBar({
  segments,
  height = 22,
  showLabels = true,
}: {
  segments: Segment[]
  height?: number
  showLabels?: boolean
}) {
  const total = segments.reduce((a, s) => a + s.value, 0) || 1
  return (
    <div style={{ display: 'flex', height, borderRadius: 3, overflow: 'hidden' }}>
      {segments.map((s) => {
        const share = s.value / total
        if (share <= 0) return null
        return (
          <div
            key={s.key}
            title={`${s.label}: ${(share * 100).toFixed(1)}%`}
            style={{
              width: `${share * 100}%`,
              background: s.color,
              display: 'grid',
              placeContent: 'center',
            }}
          >
            {showLabels && share > 0.08 && (
              <span
                className="mono"
                style={{
                  fontSize: 'var(--t-micro)',
                  // Each ordinal step carries its own label colour. A single rule
                  // ("dark on the lightest, white on the rest") breaks in dark mode,
                  // where all three steps of this ramp are light blues.
                  color: s.fg ?? '#fff',
                }}
              >
                {(share * 100).toFixed(0)}%
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}

/* ------------------------------------------------- value bar with average tick */

/** A bar with a tick marking the cohort average — used on the supervisor cards. */
export function TickBar({
  value,
  max,
  average,
  color = 'var(--accent)',
}: {
  value: number | null
  max: number
  average: number | null
  color?: string
}) {
  const pct = value === null || max === 0 ? 0 : Math.max(0, Math.min(1, value / max))
  const avg = average === null || max === 0 ? null : Math.max(0, Math.min(1, average / max))
  return (
    <div
      style={{
        position: 'relative',
        height: 8,
        background: 'var(--surface-3)',
        borderRadius: 2,
      }}
    >
      <div
        style={{ width: `${pct * 100}%`, height: '100%', background: color, borderRadius: 2 }}
      />
      {avg !== null && (
        <div
          title="cohort average"
          style={{
            position: 'absolute',
            left: `${avg * 100}%`,
            top: -2,
            width: 2,
            height: 12,
            background: 'var(--ink-2)',
          }}
        />
      )}
    </div>
  )
}

/** Chart card with a pop-out. Pop-out shows the same chart plus a value table. */
export function ChartCard({
  title,
  note,
  controls,
  children,
  large,
  table,
  png = true,
}: {
  /** Heads the card, and always heads the enlarged view. */
  title: string
  note?: string
  /** Shown in the card's header in place of the title — a picker, say. */
  controls?: ReactNodeLike
  children: ReactNodeLike
  /** What the full-page view draws instead of `children` — the same chart, taller. */
  large?: ReactNodeLike
  table?: ReactNodeLike
  /** Offer Save PNG in the enlarged view. Off when the card is showing a table. */
  png?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [plot, setPlot] = useState<HTMLDivElement | null>(null)
  const [saveNote, setSaveNote] = useState<string | null>(null)
  usePopOutLifecycle(open, () => setOpen(false))

  /*
   * PNG of whatever is on screen in the full-page view: the SVG plus the legend
   * under it, read back off the DOM so the export shows exactly the series the
   * reader left visible.
   */
  const savePng = async () => {
    const svg = plot?.querySelector('svg')
    if (!svg) return
    const legend: LegendEntry[] = [...(plot?.querySelectorAll('button[aria-pressed="true"]') ?? [])]
      .map((b) => ({
        colour: getComputedStyle(b.querySelector('span') as Element).backgroundColor,
        label: b.textContent?.trim() ?? '',
      }))
      .filter((e) => e.label)
    const stem = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
    const result = await downloadSVGAsPNG(svg, `${stem}.png`, { title, subtitle: note, legend })
    setSaveNote(result === 'ok' ? null : 'This view cannot start a download — an embedded preview blocks it.')
  }

  return (
    <>
      <div className="card" style={{ position: 'relative' }}>
        {/* Title and note stack. Side by side, the note squeezed narrow titles into
            wrapping mid-phrase ("Cost per / billed claim") on the small multiples. */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {controls ?? (
              <h3 className="card-heading" style={{ textWrap: 'balance' }}>
                {title}
              </h3>
            )}
            {note && (
              <div
                style={{
                  fontSize: 'var(--t-micro)',
                  color: 'var(--ink-3)',
                  marginTop: 3,
                }}
              >
                {note}
              </div>
            )}
          </div>
          <span style={{ marginLeft: 'auto' }}>
            <EnlargeButton compact onClick={() => setOpen(true)} />
          </span>
        </div>
        <div style={{ marginTop: 12 }}>{children}</div>
      </div>

      {open && (
        // The whole viewport, not a dialog in the middle of it: an enlarged chart is
        // for reading detail, and a 1100px box left a third of the screen unused.
        <div className="popout is-open" role="dialog" aria-modal aria-label={title}>
          <PopOutHead
            title={title}
            note={note}
            onClose={() => setOpen(false)}
            actions={
              png ? (
                <button type="button" className="btn" onClick={savePng}>
                  Save PNG
                </button>
              ) : undefined
            }
          />
          {saveNote && (
            <p className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--crit-ink)', marginTop: 0 }}>
              {saveNote}
            </p>
          )}
          <div ref={setPlot} style={{ marginTop: 4, flex: 1, minHeight: 0 }}>
            {large ?? children}
          </div>
          {table && <div style={{ marginTop: 22 }}>{table}</div>}
        </div>
      )}
    </>
  )
}

type ReactNodeLike = import('react').ReactNode

/** Full-screen scrim. Esc and a backdrop click both close — every overlay. */
export function Scrim({
  children,
  onClose,
}: {
  children: ReactNodeLike
  onClose: () => void
}) {
  // useEffect, not useMemo: a listener registered during render never cleans up.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(8,11,15,.52)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 40,
        padding: 20,
      }}
    >
      {children}
    </div>
  )
}

/** Shared with charts that live in their own files (e.g. StackColumns). */
export { FloatingTip }
